/**
 * Analytics module for Google Analytics 4 integration
 *
 * This module handles:
 * 1. User consent management for analytics
 * 2. Google Analytics initialization
 * 3. Page view and event tracking
 *
 * Analytics will only be initialized if:
 * - User has given explicit consent (stored in localStorage)
 * - Environment is production OR VITE_ENABLE_ANALYTICS=true
 */

import ReactGA from 'react-ga4';
import { ENABLE_ANALYTICS, GA_MEASUREMENT_ID } from '../constants/config';

// Constants for analytics consent
export const ANALYTICS_CONSENT_KEY = 'site-analytics-consent';

/**
 * Initialize Google Analytics based on environment and user consent
 */
export const initializeAnalytics = (): void => {
  const isProduction = import.meta.env.PROD;
  const hasConsent = localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true';

  // Only initialize if in production or analytics explicitly enabled, and user has given consent
  if ((isProduction || ENABLE_ANALYTICS) && hasConsent) {
    // Enable debug mode in development environment
    const debugMode = !isProduction;
    ReactGA.initialize(GA_MEASUREMENT_ID, { testMode: debugMode });
  }
};

/**
 * Initialize Google Analytics
 */
export const initGA = () => {
  // You can add your GA4 measurement ID here when ready
  // ReactGA.initialize('G-XXXXXXXXXX');
};

/**
 * Track page views
 * @param path - The current page path
 */
export const trackPageView = (path: string): void => {
  const hasConsent = localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true';
  if (hasConsent) {
    ReactGA.send({ hitType: 'pageview', page: path });
  }
};

/**
 * Track route changes
 * @param path - The current page path
 */
export const trackRouteChange = (path: string): void => {
  // Track route changes for user behavior analysis
  ReactGA.event({
    category: 'Navigation',
    action: 'Route Change',
    label: path,
  });

  // Also track as pageview
  trackPageView(path);
};

/**
 * Track tool usage
 * @param toolName - The name of the tool
 * @param action - The action performed on the tool (e.g., 'view', 'click')
 */
export const trackToolUsage = (
  toolName: string,
  action: string = 'view'
): void => {
  ReactGA.event({
    category: 'Tool Usage',
    action: action,
    label: toolName,
  });
};

/**
 * Track button clicks
 * @param buttonName - The name of the button
 * @param location - The location of the button (e.g., 'header', 'footer')
 */
export const trackButtonClick = (
  buttonName: string,
  location: string
): void => {
  ReactGA.event({
    category: 'Button Click',
    action: 'Click',
    label: `${buttonName} - ${location}`,
  });
};

/**
 * Set user consent for analytics
 * @param consent - Whether the user has given consent
 */
export const setAnalyticsConsent = (consent: boolean): void => {
  localStorage.setItem(ANALYTICS_CONSENT_KEY, consent ? 'true' : 'false');

  if (consent) {
    initializeAnalytics();
  }
};

/**
 * Check if user has given consent for analytics
 * @returns boolean indicating if user has given consent
 */
export const hasAnalyticsConsent = (): boolean => {
  return localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true';
};
